import { useState, useRef, useEffect } from 'react'
import ReactCrop from 'react-image-crop'
import 'react-image-crop/dist/ReactCrop.css'
import { useTranslation } from 'react-i18next'

import { ReactComponent as TrashIcon } from '@/assets/images/icons/general/trash-04.svg'
import { Modal, Button } from '@/ui'
import FlexRow from '@/ui/FlexRow'
import {
  CropWrapper,
  Wrapper,
  ImageWrapper,
  PreviewImage,
  ButtonsWrapper,
} from './Styles'

interface CropModalProps {
  src?: string
  defaultSrc?: string
  onCancel: () => void
  onSubmit: (croppedBlob: File | null) => Promise<void>
  accept?: string
  croppable?: boolean
  cropInitially?: boolean
}

let imageRef: any
let fileUrl: any

const CropModal = ({
  src,
  defaultSrc,
  onCancel,
  onSubmit,
  accept,
  croppable,
  cropInitially = false,
}: CropModalProps) => {
  const { t } = useTranslation()

  const inputRef = useRef<HTMLInputElement>(null)

  const [cropMode, setCropMode] = useState(cropInitially)

  const [cropSrc, setCropSrc] = useState(src)

  // The crop object used by ReactCrop
  const [crop, setCrop] = useState<ReactCrop.Crop>()

  // The blob that will be passed to onSubmit
  const [cropBlob, setCropBlob] = useState<Blob | null>()
  useEffect(() => {
    if (!croppable || (!src && !defaultSrc)) {
      inputRef.current?.click()
    }
    if (cropSrc !== src) {
      setCropSrc(src)
      setCropMode(true)
    }
  }, [croppable, src, defaultSrc])

  // Center the crop area on initial load
  const onImageLoaded = (image: any) => {
    imageRef = image

    const size = Math.min(image.width, image.height)

    // Center a square percent crop.
    const width =
      image.width > image.height ? (image.height / image.width) * 100 : 100
    const height =
      image.height > image.width ? (image.width / image.height) * 100 : 100
    const x = width === 100 ? 0 : (100 - width) / 2
    const y = height === 100 ? 0 : (100 - height) / 2

    setCrop({
      unit: '%',
      aspect: 1,
      width,
      height,
      x,
      y,
    })

    // makeClientProp needs crop in px
    makeClientCrop({
      width: size,
      height: size,
      x: (image.width / 100) * x,
      y: (image.height / 100) * y,
    })

    return false
  }

  const onCropChange = (crop: any, percentCrop: ReactCrop.PercentCrop) =>
    setCrop(percentCrop)

  const makeClientCrop = async (crop: ReactCrop.Crop) => {
    if (imageRef && crop.width && crop.height) {
      await getCroppedImg(imageRef, crop, 'newFile.webp')
    }
  }

  const getCroppedImg = (
    image: HTMLImageElement,
    crop: ReactCrop.Crop,
    fileName: string,
  ) => {
    const canvas = document.createElement('canvas')
    const scaleX = image.naturalWidth / image.width
    const scaleY = image.naturalHeight / image.height
    canvas.width = crop.width ?? 0
    canvas.height = crop.height ?? 0
    const ctx = canvas.getContext('2d')

    ctx &&
      ctx.drawImage(
        image,
        crop.x! * scaleX,
        crop.y! * scaleY,
        crop.width! * scaleX,
        crop.height! * scaleY,
        0,
        0,
        crop.width!,
        crop.height!,
      )

    return new Promise(resolve => {
      canvas.toBlob(blob => {
        if (!blob) {
          //reject(new Error('Canvas is empty'));
          console.error('Canvas is empty')
          return
        }
        blob['name'] = fileName
        console.log(blob)
        window.URL.revokeObjectURL(fileUrl)
        fileUrl = window.URL.createObjectURL(blob)

        setCropBlob(blob)

        resolve(fileUrl)
      }, 'image/webp')
    })
  }

  const removeImage = () => {
    setCropSrc(defaultSrc)
    setCropBlob(null)
  }

  const inputNode = (
    <input
      ref={inputRef}
      type="file"
      accept={accept ? accept : 'image/*'}
      style={{ display: 'none' }}
      onChange={({ target: { files } }) => {
        const file = files && files[0]
        if (!file) return
        const reader = new FileReader()
        reader.addEventListener('load', () => {
          if (croppable) {
            setCropSrc(reader.result as string)
            setCropMode(true)
          } else {
            onSubmit(file)
          }
        })
        reader.readAsDataURL(file)
      }}
    />
  )

  if (!croppable || (!cropSrc && !src && !defaultSrc)) {
    return inputNode
  }

  return (
    <Modal
      noPageHeader
      className="crop-modal"
      onClose={onCancel}
      closeOnDimmerClick={false}
      style={{
        width: 'auto',
      }}>
      <Modal.Content image>
        <Modal.Description style={{ width: '100%', textAlign: 'center' }}>
          {inputNode}

          {cropMode ? (
            <CropWrapper>
              <ReactCrop
                src={cropSrc ?? ''}
                crop={crop}
                onImageLoaded={onImageLoaded}
                onComplete={makeClientCrop}
                onChange={onCropChange}
              />
            </CropWrapper>
          ) : (
            <Wrapper>
              <ImageWrapper>
                <PreviewImage src={cropSrc || defaultSrc} />
              </ImageWrapper>
              <ButtonsWrapper>
                <Button.Add
                  fluid
                  content={t('Upload new image')}
                  onClick={() => inputRef.current?.click()}
                />
                <Button.Cancel
                  fluid
                  leftIcon={<TrashIcon />}
                  content={t('Remove image')}
                  onClick={removeImage}
                  disabled={!src || cropSrc === defaultSrc}
                />
              </ButtonsWrapper>
            </Wrapper>
          )}
        </Modal.Description>
      </Modal.Content>
      <Modal.Actions>
        <FlexRow noMargin style={{ padding: '0 .5em' }}>
          <Button.Cancel onClick={onCancel} content={t('Cancel')} />
          <Button
            onClick={() => onSubmit(cropBlob ? (cropBlob as File) : null)}
            content={t('Save & close')}
          />
        </FlexRow>
      </Modal.Actions>
    </Modal>
  )
}

export default CropModal
